import { useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import PixDialog from "@/components/PixDialog";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CreditCard, Sparkles, Calendar, CheckCircle2, Loader2, Clock, Check, Zap, QrCode } from "lucide-react";

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const statusLabel = {
  trial: { text: "Gratuito", color: "text-brand-blue bg-blue-50" },
  trial_expired: { text: "Encerrado", color: "text-red-500 bg-red-50" },
  authorized: { text: "Ativo", color: "text-brand-green bg-emerald-50" },
  active: { text: "Ativo", color: "text-brand-green bg-emerald-50" },
  pending: { text: "Aguardando pagamento", color: "text-brand-yellow bg-amber-50" },
  canceled: { text: "Cancelado", color: "text-brand-muted bg-slate-100" },
  none: { text: "Sem assinatura", color: "text-brand-muted bg-slate-100" },
};

export default function Assinatura() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState(null);
  const [single, setSingle] = useState(89.9);
  const [pixOpen, setPixOpen] = useState(false);
  const [pixOrder, setPixOrder] = useState(null);
  const [pixNote, setPixNote] = useState("");

  const load = () => api.get("/subscription/me").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/plans").then((r) => { setPlans(r.data.plans); setSingle(r.data.single_quiz_price); });
  }, []);

  const subscribe = async (planId) => {
    setBusy(`card-${planId}`);
    try {
      const res = await api.post("/payments/subscribe", {
        plan_id: planId,
        billing: yearly ? "yearly" : "monthly",
        back_url: window.location.origin + "/assinatura",
      });
      window.location.assign(res.data.init_point);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar assinatura.");
      setBusy(null);
    }
  };

  const subscribePix = async (planId) => {
    setBusy(`pix-${planId}`);
    setPixOrder(null);
    setPixNote("Cobrança mensal via Pix: você recebe uma NOVA cobrança a cada mês e precisa pagá-la manualmente. Não é débito automático.");
    setPixOpen(true);
    try {
      const res = await api.post("/payments/pix/subscription", { plan_id: planId });
      setPixOrder(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar cobrança Pix.");
      setPixOpen(false);
    } finally {
      setBusy(null);
    }
  };

  const buySingle = async () => {
    setBusy("card-single");
    try {
      const res = await api.post("/payments/single-quiz", { back_url: window.location.origin + "/assinatura" });
      window.location.assign(res.data.init_point);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar pagamento.");
      setBusy(null);
    }
  };

  const buySinglePix = async () => {
    setBusy("pix-single");
    setPixOrder(null);
    setPixNote("");
    setPixOpen(true);
    try {
      const res = await api.post("/payments/pix/single-quiz");
      setPixOrder(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar cobrança Pix.");
      setPixOpen(false);
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    try {
      await api.delete("/payments/subscription");
      toast.success("Assinatura cancelada.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao cancelar.");
    }
  };

  if (!data) {
    return <DashboardLayout><div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-brand-blue" /></div></DashboardLayout>;
  }

  const { access, ai_used, payments, quiz_credits } = data;
  const st = statusLabel[access.status] || statusLabel.none;
  const renewal = access.current_period_end ? new Date(access.current_period_end).toLocaleDateString("pt-BR") : "—";
  const showPlans = !["authorized", "active"].includes(access.status);

  return (
    <DashboardLayout>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center"><CreditCard className="w-6 h-6 text-brand-blue" /></div>
        <div>
          <h1 className="font-heading text-3xl font-black text-brand-ink">Minha assinatura</h1>
          <p className="text-brand-muted text-sm">Gerencie seu plano e pagamentos</p>
        </div>
      </div>

      {access.status === "trial_expired" && (
        <div className="mt-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-5">
          <Clock className="w-6 h-6 shrink-0" />
          <div>
            <p className="font-heading font-bold">Seu período gratuito terminou</p>
            <p className="text-sm">Escolha uma assinatura abaixo ou compre um quiz avulso para continuar.</p>
          </div>
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-brand-ink">Plano atual</h3>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${st.color}`}>{st.text}</span>
          </div>
          <p className="mt-4 font-heading text-3xl font-black text-brand-ink">{access.plan_name}</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 text-brand-ink"><CheckCircle2 className="w-4 h-4 text-brand-green" /> Até {access.max_players} participantes</div>
            <div className="flex items-center gap-2 text-brand-ink"><Sparkles className="w-4 h-4 text-brand-purple" /> {ai_used} de {access.ai_quota} questões IA</div>
            <div className="flex items-center gap-2 text-brand-ink"><Calendar className="w-4 h-4 text-brand-blue" /> Próxima data: {renewal}</div>
            {access.status === "active" && (
              <div className="flex items-center gap-2 text-brand-ink"><QrCode className="w-4 h-4 text-brand-green" /> Pagamento via Pix (mensal manual)</div>
            )}
            {quiz_credits > 0 && (
              <div className="flex items-center gap-2 text-brand-ink" data-testid="quiz-credits"><Sparkles className="w-4 h-4 text-brand-yellow" /> {quiz_credits} crédito(s) de quiz avulso</div>
            )}
          </div>
          {(access.status === "authorized" || access.status === "active") && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="mt-6 rounded-full text-red-500 border-red-200 hover:bg-red-50" data-testid="cancel-sub-btn">Cancelar assinatura</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancelar assinatura?</AlertDialogTitle>
                  <AlertDialogDescription>Seus quizzes serão preservados, mas os limites do plano deixarão de valer para novas sessões.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Voltar</AlertDialogCancel>
                  <AlertDialogAction onClick={cancel} className="bg-red-500 hover:bg-red-600">Cancelar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        <div className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
          <h3 className="font-heading font-bold text-brand-ink">Histórico de pagamentos</h3>
          {payments && payments.length ? (
            <ul className="mt-4 space-y-3">
              {payments.map((p, i) => (
                <li key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                  <span className="text-brand-muted">{new Date(p.created_at).toLocaleDateString("pt-BR")}</span>
                  <span className="font-semibold text-brand-ink">{fmt(p.amount)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-brand-muted">Nenhum pagamento registrado ainda.</p>
          )}
        </div>
      </div>

      {showPlans && (
        <div className="mt-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h2 className="font-heading text-2xl font-black text-brand-ink">Escolha seu plano</h2>
            <div className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1">
              <button onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${!yearly ? "bg-brand-blue text-white" : "text-brand-muted"}`}>Mensal</button>
              <button onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${yearly ? "bg-brand-blue text-white" : "text-brand-muted"}`}>Anual <span className="text-brand-green">-16%</span></button>
            </div>
          </div>
          <div className="mt-6 grid lg:grid-cols-3 gap-5">
            {plans.map((plan) => {
              const price = yearly ? plan.price_yearly / 12 : plan.price_monthly;
              return (
                <div key={plan.plan_id} className={`bg-white rounded-2xl p-7 border ${plan.popular ? "border-brand-purple ring-2 ring-brand-purple" : "border-slate-100"} shadow-sm`}>
                  {plan.popular && <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-purple text-white text-xs font-bold uppercase mb-3"><Zap className="w-3 h-3" /> Popular</span>}
                  <h3 className="font-heading font-extrabold text-lg text-brand-ink">{plan.name}</h3>
                  <div className="mt-3 flex items-end gap-1">
                    <span className="font-heading text-3xl font-black text-brand-ink">{fmt(price)}</span>
                    <span className="text-brand-muted mb-1 text-sm">/mês</span>
                  </div>
                  <p className="mt-1 text-xs text-brand-muted">Até {plan.max_players} participantes · {plan.ai_quota} questões IA</p>
                  <Button onClick={() => subscribe(plan.plan_id)} disabled={busy === `card-${plan.plan_id}`} className={`mt-5 w-full rounded-full h-11 font-semibold ${plan.popular ? "bg-brand-purple hover:bg-purple-700" : "bg-brand-blue hover:bg-blue-700"}`} data-testid={`subscribe-${plan.plan_id}`}>
                    {busy === `card-${plan.plan_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1.5" /> Assinar com cartão</>}
                  </Button>
                  <Button onClick={() => subscribePix(plan.plan_id)} disabled={busy === `pix-${plan.plan_id}`} variant="outline" className="mt-2 w-full rounded-full h-11 font-semibold border-brand-green text-brand-green hover:bg-emerald-50" data-testid={`subscribe-pix-${plan.plan_id}`}>
                    {busy === `pix-${plan.plan_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <><QrCode className="w-4 h-4 mr-1.5" /> Pagar com Pix (mensal)</>}
                  </Button>
                  <p className="mt-2 text-[11px] text-brand-muted leading-snug">Pix: cobrança mensal manual — você paga uma nova cobrança a cada mês (sem débito automático).</p>
                </div>
              );
            })}
          </div>

          <div className="mt-6 bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center"><Sparkles className="w-6 h-6 text-brand-yellow" /></div>
              <div>
                <h3 className="font-heading font-bold text-brand-ink">Prefere um quiz avulso?</h3>
                <p className="text-sm text-brand-muted">Sem assinatura — pague apenas por um quiz.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-heading text-2xl font-black text-brand-ink">{fmt(single)}</span>
              <Button onClick={buySingle} disabled={busy === "card-single"} variant="outline" className="rounded-full border-brand-yellow text-brand-yellow hover:bg-amber-50 gap-1.5" data-testid="buy-single-btn">
                {busy === "card-single" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4" /> Cartão</>}
              </Button>
              <Button onClick={buySinglePix} disabled={busy === "pix-single"} className="rounded-full bg-brand-green hover:bg-emerald-600 gap-1.5" data-testid="buy-single-pix-btn">
                {busy === "pix-single" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><QrCode className="w-4 h-4" /> Pix</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      <PixDialog
        open={pixOpen}
        onOpenChange={setPixOpen}
        order={pixOrder}
        note={pixNote}
        onApproved={load}
      />
    </DashboardLayout>
  );
}
